import { queryOptions } from '@tanstack/react-query'
import { z } from 'zod'

import { api } from '@/shared/api/http'
import { PAGE_MAX_LIMIT, type CategoryOut, type Page } from '@/shared/api/types'

export type CategoryKind = 'income' | 'expense'

/**
 * Grupos do fluxo de caixa (DFC) — os 5 FIXOS da Nibo, na ordem de exibição.
 * Independentes de kind: um grupo pode ter entrada E saída. São a chave que o
 * painel de acompanhamento soma.
 */
export const CATEGORY_CASH_FLOW_GROUPS = [
  'operating_revenue',
  'operating_cost',
  'operating_expense',
  'investing',
  'financing',
] as const
export type CategoryCashFlowGroup = (typeof CATEGORY_CASH_FLOW_GROUPS)[number]

export const categoryKeys = {
  all: () => ['categories'] as const,
  lists: () => [...categoryKeys.all(), 'list'] as const,
  list: (kind?: CategoryKind) => [...categoryKeys.lists(), kind ?? 'all'] as const,
} as const

/**
 * Categorias de um kind, para o seletor do agendamento.
 *
 * O backend exige categoria COMPATÍVEL com o tipo do agendamento
 * (payable→expense, receivable→income; senão 422 schedule_category_kind_mismatch).
 * Por isso o seletor filtra por kind — só oferece o que o backend aceitaria.
 *
 * limit=MAX: o plano de contas raramente passa de algumas dezenas; carregar tudo
 * de uma vez evita paginar um dropdown.
 */
export const categoriesQueryOptions = (kind?: CategoryKind) =>
  queryOptions({
    queryKey: categoryKeys.list(kind),
    queryFn: ({ signal }) =>
      // kind ausente = plano de contas COMPLETO (receitas + despesas), usado na
      // tela de Configurações › Categorias, que agrupa por natureza numa página
      // só. O http descarta params undefined, então some do querystring.
      api.get<Page<CategoryOut>>('/v1/categories', { kind, limit: PAGE_MAX_LIMIT }, signal),
    // O plano de contas muda pouco; 5 min sem refetch é seguro.
    staleTime: 5 * 60_000,
  })

/**
 * Nova categoria. CategoryCreate = {name, kind, parent_id?}.
 * kind é imutável depois (o backend rejeita alterá-lo), então é escolhido só na
 * criação — na prática pela aba ativa (Receita/Despesa).
 */
export const categoryFormSchema = z.object({
  name: z.string().trim().min(1, 'validation:required').max(120, 'validation:maxLength'),
  // Natureza da categoria. Escolhida SÓ na criação (o backend a torna imutável
  // depois — enviá-la num PATCH é 422 category_kind_immutable), por isso não
  // entra em `toCategoryUpdateBody`.
  kind: z.enum(['income', 'expense']),
  // Grupo do fluxo de caixa (DFC) — obrigatório, EDITÁVEL pelo lápis.
  cashFlowGroup: z.enum(CATEGORY_CASH_FLOW_GROUPS),
})

export type CategoryFormValues = z.infer<typeof categoryFormSchema>

/** POST /v1/categories — cadastro, sem Idempotency-Key. */
export function createCategory(v: CategoryFormValues): Promise<CategoryOut> {
  return api
    .post<CategoryOut>('/v1/categories', {
      body: {
        name: v.name,
        kind: v.kind,
        cash_flow_group: v.cashFlowGroup,
      },
    })
    .then((r) => r.data)
}

/**
 * Corpo de `PATCH /v1/categories/{id}`.
 *
 * ⚠️ `kind` é IMUTÁVEL: enviá-lo (mesmo com o valor atual) → 422
 * `category_kind_immutable`. Por isso o corpo tem SÓ `name` e `cash_flow_group`
 * — nunca `kind`. O grupo é editável (reclassificação pelo lápis).
 */
export interface CategoryUpdateBody {
  readonly name: string
  readonly cash_flow_group: CategoryCashFlowGroup
}

export function toCategoryUpdateBody(v: CategoryFormValues): CategoryUpdateBody {
  return { name: v.name, cash_flow_group: v.cashFlowGroup }
}

/** PATCH /v1/categories/{id} — edição de cadastro; sem Idempotency-Key. */
export function updateCategory(id: string, v: CategoryFormValues): Promise<CategoryOut> {
  return api.patch<CategoryOut>(`/v1/categories/${id}`, toCategoryUpdateBody(v))
}

/**
 * DELETE /v1/categories/{id} — SOFT delete (marca deleted_at; some da UI, o
 * registro fica para preservar agendamentos passados).
 *
 * Pode falhar com 409:
 *   - category_has_children — tem subcategorias ativas (sem cascata: trate os
 *     filhos primeiro)
 *   - category_in_use — está vinculada a algum agendamento
 * A tela trata esses códigos com mensagem específica.
 */
export function deleteCategory(id: string): Promise<void> {
  return api.del(`/v1/categories/${id}`)
}
